import React from "react";

import { Ad } from "./AdModel";

/**
 * Minimal card shape consumed by the merge helper. Declared locally so this
 * module does not import from `useCommentCards` (which would create a cycle).
 * Fields match `Card` from `App/common/useCommentCards`.
 */
export type MergeableCard = {
  id: string;
  createdAt: number;
  timestamp: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  comment?: any;
  renderComponent?: () => JSX.Element;
};

export type AdCardRenderer = (ad: Ad) => () => JSX.Element;

export type MergeAdsIntoCardsParams = {
  cards: MergeableCard[];
  ads: Ad[];
  renderAd: AdCardRenderer;
  /**
   * Every `interval` cards, inject the next eligible ad. Defaults to 5.
   */
  interval?: number;
  /**
   * Show at least this many real cards before the first ad. Defaults to 3.
   */
  minBefore?: number;
};

/**
 * Returns a new cards list with ads interleaved according to `interval` and
 * `minBefore`. The server is authoritative on ad ordering (priority), so we
 * do not re-sort client-side — we consume `ads` as-is.
 *
 * Port of `mergeAdsIntoComments` from the Flutter `CommentsRepository`:
 *   - If there are no ads, returns the input cards unchanged.
 *   - If there are fewer than `minBefore` cards, returns unchanged (we never
 *     force an ad in front of an audience that hasn't seen enough content).
 *   - First ad appears after `minBefore` cards; subsequent ads appear every
 *     `interval` cards after the previous ad. Stops when ads are exhausted.
 *
 * Each injected ad becomes a card whose id is prefixed `ad-` and whose
 * `renderComponent` is the caller-supplied renderer. This keeps the ad card
 * type compatible with the existing carousel which renders
 * `card.renderComponent ?? <CommentCardPreview />`.
 */
export const mergeAdsIntoCards = ({
  cards,
  ads,
  renderAd,
  interval = 5,
  minBefore = 3,
}: MergeAdsIntoCardsParams): MergeableCard[] => {
  if (ads.length === 0 || cards.length < minBefore) return cards;

  const result: MergeableCard[] = [];
  let adIndex = 0;
  let cardsSinceLastAd = 0;
  let firstAdPlaced = false;

  for (const card of cards) {
    result.push(card);
    cardsSinceLastAd += 1;

    const threshold = firstAdPlaced ? interval : minBefore;
    if (cardsSinceLastAd >= threshold && adIndex < ads.length) {
      const ad = ads[adIndex];
      result.push({
        // Prefix distinguishes ad cards from real comment ids and keeps
        // `key` stable across renders without colliding with other cards.
        id: `ad-${ad.id}`,
        // Inject ads immediately after the preceding card so ordering in the
        // carousel matches the feed order the user sees scrolling.
        createdAt: card.createdAt,
        timestamp: card.timestamp,
        renderComponent: renderAd(ad),
      });
      adIndex += 1;
      cardsSinceLastAd = 0;
      firstAdPlaced = true;
    }
  }

  return result;
};

// Re-export for consumers that want the React type without directly
// importing React from every caller site.
export type { React };
